import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  ArrowLeft, FileText, Check, AlertTriangle, 
  ChevronDown, Pen, CheckCircle, Download 
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
import { executedCopyHref } from '../../utils/executedCopyUrl';
const ContractsPage = () => {
  const navigate = useNavigate();
  const [pendingContracts, setPendingContracts] = useState([]);
  const [acceptedContracts, setAcceptedContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [signatureData, setSignatureData] = useState({ name: '', drawing: null });
  // Iter-298 — Company attribution captured for client/agency signers.
  const [companyAttribution, setCompanyAttribution] = useState({
    company_name: '',
    signer_title: '',
    signer_email: '',
  });
  const [authorityCertified, setAuthorityCertified] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [signerPrefillStatus, setSignerPrefillStatus] = useState(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const pdfContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    fetchContracts();
    fetchSignerContext();
  }, []);

  const fetchSignerContext = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const meRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { headers });
      const me = meRes.ok ? await meRes.json() : null;
      const role = (me?.role || '').toLowerCase();
      setUserRole(role);
      if (me?.email) {
        setCompanyAttribution((p) => ({ ...p, signer_email: p.signer_email || me.email }));
      }
      if (role === 'client' || role === 'agency') {
        const sigRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/client/signature-status`, { headers });
        if (sigRes.ok) {
          const sig = await sigRes.json();
          setSignerPrefillStatus(sig);
          const latest = sig?.latest;
          if (latest) {
            setCompanyAttribution((p) => ({
              company_name: p.company_name || latest.company_name || '',
              signer_title: p.signer_title || latest.signer_title || '',
              signer_email: p.signer_email || latest.signer_email || (me?.email || ''),
            }));
            if (latest.signature_name) {
              setSignatureData((p) => ({ ...p, name: p.name || latest.signature_name }));
            }
          }
        }
      }
    } catch (e) {
      // best-effort prefill — never block the page
    }
  };

  const fetchContracts = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [pendingRes, acceptedRes, icmaRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/pending`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/accepted`, { headers }),
        // 2026-08-30 P0 — Hardened e-sign executed copies from
        // /api/icma/my-acceptances (talent only). Merged into the
        // Accepted Contracts list so signers can retrieve immutable
        // executed copies + certificate at any time.
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/icma/my-acceptances`, { headers }).catch(() => null),
      ]);

      const pending = await pendingRes.json();
      const accepted = await acceptedRes.json();
      let icmaAcc = [];
      if (icmaRes && icmaRes.ok) {
        try { icmaAcc = await icmaRes.json(); } catch (_) { icmaAcc = []; }
      }
      // Map ICMA rows to the same shape the Accepted Contracts list expects
      const icmaMapped = (Array.isArray(icmaAcc) ? icmaAcc : []).map((c) => ({
        id: c.acceptance_id,
        contract_title: c.title || 'iEvntür Independent Contractor and Marketplace Agreement',
        contract_version: c.agreement_version,
        signed_at: c.accepted_at,
        signature_name: c.signed_legal_name,
        executed_copy_url: c.executed_copy_url,
        executed_copy_hash: c.executed_copy_hash,
        effective_date: c.effective_date,
        is_icma_executed: true,
      }));

      setPendingContracts(pending.contracts || []);
      setAcceptedContracts([...icmaMapped, ...(accepted || [])]);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const element = e.target;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (isAtBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#06b6d4';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(prev => ({
        ...prev,
        drawing: canvas.toDataURL()
      }));
    }
  };

  const clearSignature = () => {
    initCanvas();
    setSignatureData(prev => ({ ...prev, drawing: null }));
  };

  const handleDownloadAccepted = async (acceptanceId) => {
    const token = localStorage.getItem('token');
    try {
      toast.loading('Generating PDF...', { id: `dl-${acceptanceId}` });
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contracts/download-accepted/${acceptanceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signed-contract-${acceptanceId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.dismiss(`dl-${acceptanceId}`);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.dismiss(`dl-${acceptanceId}`);
      toast.error(`Failed to download PDF (${err.message || 'unknown error'})`);
    }
  };

  const handleAccept = async () => {
    if (!signatureData.name.trim()) {
      toast.error('Please type your full name');
      return;
    }

    // Iter-298 — client + agency must capture company attribution.
    const audience = (selectedContract?.audience || '').toLowerCase();
    const requiresCompany =
      userRole === 'client' || userRole === 'agency' ||
      audience === 'client' || audience === 'both';
    if (requiresCompany) {
      if (!companyAttribution.company_name.trim()) {
        toast.error('Company name is required');
        return;
      }
      if (!companyAttribution.signer_title.trim()) {
        toast.error("Signer's title is required (e.g. 'VP, Marketing')");
        return;
      }
      const e = companyAttribution.signer_email.trim();
      if (!e || !/.+@.+\..+/.test(e)) {
        toast.error('A valid signer email is required');
        return;
      }
    }

    setSigning(true);
    const token = localStorage.getItem('token');

    try {
      const payload = {
        contract_id: selectedContract.id,
        signature_name: signatureData.name,
        signature_image_url: signatureData.drawing,
      };
      if (requiresCompany) {
        if (!authorityCertified) {
          toast.error('You must certify that you are an authorized representative of this company.');
          setSigning(false);
          return;
        }
        payload.company_name = companyAttribution.company_name.trim();
        payload.signer_title = companyAttribution.signer_title.trim();
        payload.signer_email = companyAttribution.signer_email.trim();
        payload.authority_certified = true;
        if (signerPrefillStatus?.needs_resign && signerPrefillStatus?.reason) {
          payload.re_sign_reason = signerPrefillStatus.reason;
        }
      }
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let detail = 'Failed to accept contract';
        try { const j = await response.json(); detail = j?.detail || detail; } catch { /* noop */ }
        throw new Error(typeof detail === 'string' ? detail : 'Failed to accept contract');
      }

      toast.success('Contract accepted successfully!');
      setSelectedContract(null);
      setSignatureData({ name: '', drawing: null });
      setHasScrolledToBottom(false);
      fetchContracts();
      fetchSignerContext();
    } catch (error) {
      console.error('Error accepting contract:', error);
      toast.error(error?.message || 'Failed to accept contract');
    } finally {
      setSigning(false);
    }
  };

  useEffect(() => {
    if (selectedContract && canvasRef.current) {
      initCanvas();
    }
  }, [selectedContract]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#03DFFC]"></div>
      </div>
    );
  }

  // Contract signing modal
  if (selectedContract) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="contract-signing-modal">
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
            <Button 
              variant="ghost" 
              onClick={() => {
                setSelectedContract(null);
                setHasScrolledToBottom(false);
                setSignatureData({ name: '', drawing: null });
              }} 
              className="text-[#03DFFC] hover:text-[#03DFFC]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <span className="text-gray-900 font-medium">{selectedContract.title}</span>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Contract Preview */}
          <Card className="bg-white border-gray-200 mb-6">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#03DFFC]" />
                {selectedContract.title}
              </CardTitle>
              <p className="text-sm text-gray-500">Version {selectedContract.version}</p>
            </CardHeader>
            <CardContent>
              {/* Scrollable PDF/Contract Content Area */}
              <div 
                ref={pdfContainerRef}
                onScroll={handleScroll}
                className="bg-gray-50 rounded-lg p-6 h-96 overflow-y-auto"
                data-testid="contract-content"
              >
                <div className="text-gray-600 space-y-4">
                  <p className="font-medium text-lg text-gray-900">Agreement Terms</p>
                  <p>{selectedContract.description || 'Please review the contract terms below.'}</p>
                  
                  {/* PDF Viewer Link — always available via /view bridge */}
                  <div className="bg-slate-100 rounded p-4 text-center border border-gray-200">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/contracts/view/${selectedContract.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#03DFFC] hover:underline text-lg font-medium"
                      data-testid="open-full-contract-link"
                      onClick={(e) => {
                        // Pre-flight ping so we surface failures instead of silent blank tabs
                        // (link still opens; this just logs/toasts on failure)
                        const url = e.currentTarget.href;
                        fetch(url, { method: 'HEAD' }).then((r) => {
                          if (!r.ok) toast.error(`Contract failed to open (status ${r.status}).`);
                        }).catch(() => toast.error('Contract failed to open. Check your connection.'));
                      }}
                    >
                      <FileText className="w-5 h-5" />
                      Open Full Contract in New Tab
                    </a>
                    <p className="text-xs text-gray-500 mt-2">Opens the full agreement text in a new tab (printable).</p>
                  </div>
                  
                  {/* Placeholder content to enable scrolling */}
                  <div className="space-y-4 text-sm text-gray-500">
                    <p>By signing this agreement, you acknowledge and agree to the following terms and conditions...</p>
                    <p>1. You confirm that all information provided is accurate and complete.</p>
                    <p>2. You agree to comply with all platform policies and guidelines.</p>
                    <p>3. You understand your responsibilities and obligations under this agreement.</p>
                    <p>4. You acknowledge that violation of terms may result in account suspension.</p>
                    <p>5. You agree to the platform's dispute resolution procedures.</p>
                  </div>
                </div>
                
                {/* Scroll indicator */}
                {!hasScrolledToBottom && (
                  <div className="sticky bottom-0 left-0 right-0 flex justify-center pt-4">
                    <div className="bg-cyan-900/80 text-[#03DFFC] px-4 py-2 rounded-full text-sm flex items-center gap-2">
                      <ChevronDown className="w-4 h-4 animate-bounce" />
                      Scroll to bottom to continue
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Signature Section */}
          <Card className={`bg-white border-gray-200 transition-opacity ${!hasScrolledToBottom ? 'opacity-50 pointer-events-none' : ''}`}>
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <Pen className="w-5 h-5 text-[#03DFFC]" />
                Sign Contract
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Iter-298 — Company attribution (client / agency only) */}
              {(userRole === 'client' || userRole === 'agency' ||
                (selectedContract.audience || '').toLowerCase() === 'client' ||
                (selectedContract.audience || '').toLowerCase() === 'both') &&
              signerPrefillStatus?.can_resign === false ? (
                <div className="space-y-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4" data-testid="company-already-signed-block">
                  <p className="text-sm text-emerald-900 font-medium">
                    This Service Agreement has already been signed for your company.
                  </p>
                  {signerPrefillStatus?.latest && (
                    <div className="text-xs text-emerald-900 leading-relaxed">
                      <div><strong>Company:</strong> {signerPrefillStatus.latest.company_name}</div>
                      <div><strong>Signed by:</strong> {signerPrefillStatus.latest.signature_name}{signerPrefillStatus.latest.signer_title ? ` (${signerPrefillStatus.latest.signer_title})` : ''}</div>
                      <div><strong>Signed at:</strong> {signerPrefillStatus.latest.signed_at ? new Date(signerPrefillStatus.latest.signed_at).toLocaleString() : '—'}</div>
                      <div><strong>Version:</strong> {signerPrefillStatus.latest.contract_version}</div>
                    </div>
                  )}
                  <p className="text-xs text-emerald-900">
                    You are not an authorized signer for this company, so you do not
                    need to sign this agreement again. If you need to re-sign on
                    behalf of the company, ask your iEvntür admin to add you as an
                    authorized signer.
                  </p>
                </div>
              ) : ((userRole === 'client' || userRole === 'agency' ||
                (selectedContract.audience || '').toLowerCase() === 'client' ||
                (selectedContract.audience || '').toLowerCase() === 'both') && (
                <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="company-attribution-block">
                  <p className="text-sm text-amber-900 font-medium">
                    Sign on behalf of your company. This Service Agreement is
                    binding on the company below — not just you personally.
                    All four fields are required for the signature to be
                    legally enforceable.
                  </p>
                  {signerPrefillStatus?.needs_resign && (
                    <p className="text-xs text-rose-700 font-semibold" data-testid="resign-reason-banner">
                      Re-signature required: {(signerPrefillStatus.reason || 'updates').replace(/_/g, ' ')}.
                    </p>
                  )}
                  <div className="space-y-2">
                    <Label className="text-gray-700">Company name (legal entity)</Label>
                    <Input
                      value={companyAttribution.company_name}
                      onChange={(e) => setCompanyAttribution((p) => ({ ...p, company_name: e.target.value }))}
                      placeholder="Acme Foods Inc."
                      className="bg-white border-gray-200 text-gray-900"
                      data-testid="signer-company-name"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Signer title</Label>
                      <Input
                        value={companyAttribution.signer_title}
                        onChange={(e) => setCompanyAttribution((p) => ({ ...p, signer_title: e.target.value }))}
                        placeholder="VP, Marketing"
                        className="bg-white border-gray-200 text-gray-900"
                        data-testid="signer-title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Signer email</Label>
                      <Input
                        type="email"
                        value={companyAttribution.signer_email}
                        onChange={(e) => setCompanyAttribution((p) => ({ ...p, signer_email: e.target.value }))}
                        placeholder="jane@acme.com"
                        className="bg-white border-gray-200 text-gray-900"
                        data-testid="signer-email"
                      />
                    </div>
                  </div>

                  {/* Iter-299 — Authority-to-bind certification.
                      Strengthens enforceability under ESIGN/UETA by
                      attesting that the signer is an authorized
                      representative of the company. Required for the
                      signature to commit. */}
                  <label
                    className="flex items-start gap-2 cursor-pointer p-3 rounded-md bg-white border border-amber-200 hover:border-amber-400 transition-colors"
                    data-testid="authority-cert-row"
                  >
                    <input
                      type="checkbox"
                      checked={authorityCertified}
                      onChange={(e) => setAuthorityCertified(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-amber-600 cursor-pointer"
                      data-testid="authority-cert-checkbox"
                    />
                    <span className="text-xs text-gray-800 leading-relaxed">
                      I certify that I am an authorized representative of
                      <strong className="px-1">{companyAttribution.company_name || 'this company'}</strong>
                      and have the authority to bind the company to this
                      agreement. I understand this electronic signature is
                      legally enforceable against the company under ESIGN
                      and UETA.
                    </span>
                  </label>
                </div>
              ))}

              {/* Typed Name */}
              <div className="space-y-2">
                <Label className="text-gray-600">Type your full legal name</Label>
                <Input
                  value={signatureData.name}
                  onChange={(e) => setSignatureData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  className="bg-gray-50 border-gray-200 text-gray-900"
                  data-testid="signature-name-input"
                />
              </div>

              {/* Signature Canvas */}
              <div className="space-y-2">
                <Label className="text-gray-600">Draw your signature (optional)</Label>
                <div className="relative">
                  <canvas
                    ref={canvasRef}
                    width={400}
                    height={150}
                    className="w-full rounded-lg border border-gray-200 cursor-crosshair touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    data-testid="signature-canvas"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSignature}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-900"
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {/* Agreement Checkbox and Submit */}
              <div className="pt-4 border-t border-gray-200">
                <Alert className="bg-gray-50 border-gray-200 mb-4">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <AlertDescription className="text-gray-600">
                    By clicking "Accept & Sign", you agree to the terms of this contract. 
                    Your signature will be recorded with timestamp and IP address.
                  </AlertDescription>
                </Alert>

                <Button
                  onClick={handleAccept}
                  disabled={!signatureData.name.trim() || signing}
                  className="w-full bg-[#03DFFC] hover:bg-[#03DFFC] text-black font-semibold h-12"
                  data-testid="accept-contract-btn"
                >
                  {signing ? (
                    'Processing...'
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Accept & Sign Contract
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="contracts-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="text-[#03DFFC] hover:text-[#03DFFC] hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Contracts</h1>

        {/* Pending Contracts */}
        {pendingContracts.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Pending Contracts ({pendingContracts.length})
            </h2>
            <div className="space-y-4">
              {pendingContracts.map((contract) => (
                <Card 
                  key={contract.id} 
                  className="bg-white border-gray-200 hover:border-[#03DFFC]/30 transition-colors cursor-pointer"
                  onClick={() => setSelectedContract(contract)}
                  data-testid="pending-contract-card"
                >
                  <CardContent className="p-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-[#03DFFC]" />
                      <div>
                        <h3 className="font-medium text-gray-900">{contract.title}</h3>
                        <p className="text-sm text-gray-500">
                          Version {contract.version} • {contract.is_required ? 'Required' : 'Optional'}
                        </p>
                      </div>
                    </div>
                    <Button size="sm" className="bg-[#03DFFC] hover:bg-[#03DFFC] text-black">
                      Review & Sign
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {pendingContracts.length === 0 && (
          <Alert className="mb-8 bg-green-900/30 border-green-600">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-100">
              All required contracts have been accepted!
            </AlertDescription>
          </Alert>
        )}

        {/* Accepted Contracts */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Accepted Contracts ({acceptedContracts.length})
          </h2>
          {acceptedContracts.length === 0 ? (
            <p className="text-gray-500">No contracts accepted yet.</p>
          ) : (
            <div className="space-y-4">
              {acceptedContracts.map((acceptance) => (
                <Card 
                  key={acceptance.id} 
                  className="bg-white border-gray-200 hover:border-[#03DFFC]/40 transition-colors cursor-pointer"
                  onClick={() => {
                    const tok = localStorage.getItem('token') || '';
                    const url = acceptance.is_icma_executed
                      ? executedCopyHref(acceptance.executed_copy_url)
                      : `${process.env.REACT_APP_BACKEND_URL}/api/contracts/view-accepted/${acceptance.id}?token=${encodeURIComponent(tok)}`;
                    const win = window.open(url, '_blank', 'noopener,noreferrer');
                    if (!win) {
                      toast.error('Popup blocked. Please allow popups to view the contract.');
                    }
                  }}
                  data-testid="accepted-contract-card"
                >
                  <CardContent className="p-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-green-600" />
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {acceptance.contract_title}
                          {acceptance.is_icma_executed && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded" data-testid="executed-badge">
                              Executed
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Version {acceptance.contract_version} • Signed: {formatDateMonthDay(acceptance.signed_at || acceptance.accepted_at)}
                          {acceptance.signature_name ? ` • /s/ ${acceptance.signature_name}` : ''}
                        </p>
                        {acceptance.is_icma_executed && acceptance.executed_copy_hash && (
                          <p className="text-[10px] text-gray-400 font-mono mt-0.5">exec hash: {acceptance.executed_copy_hash.slice(0, 24)}…</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          const tok = localStorage.getItem('token') || '';
                          const url = acceptance.is_icma_executed
                            ? executedCopyHref(acceptance.executed_copy_url)
                            : `${process.env.REACT_APP_BACKEND_URL}/api/contracts/view-accepted/${acceptance.id}?token=${encodeURIComponent(tok)}`;
                          const win = window.open(url, '_blank', 'noopener,noreferrer');
                          if (!win) toast.error('Popup blocked. Please allow popups to view the contract.');
                        }}
                        data-testid="view-accepted-btn"
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (acceptance.is_icma_executed) {
                            const url = executedCopyHref(acceptance.executed_copy_url);
                            const win = window.open(url, '_blank', 'noopener,noreferrer');
                            if (!win) toast.error('Popup blocked. Please allow popups to view the contract.');
                          } else {
                            handleDownloadAccepted(acceptance.id);
                          }
                        }}
                        className="text-[#03DFFC] border-[#03DFFC]/30 hover:bg-[#03DFFC]/10"
                        data-testid="download-accepted-btn"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                      <span className="text-green-600 text-sm flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> Accepted
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractsPage;
